import DOMPurify from 'dompurify';

/**
 * Sanitizes Lexical announcement HTML for individual-portal display.
 * Mirrors ms-ui sanitizeAnnouncementHtml: keeps formatting, strips XSS.
 */

const ANNOUNCEMENT_HTML_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'a',
        'strong',
        'em',
        'b',
        'i',
        'u',
        's',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'blockquote',
        'sub',
        'sup',
        'code'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'dir'],
    ALLOW_DATA_ATTR: false
};

const SAFE_STYLE_PROPERTIES = new Set([
    'color',
    'background-color',
    'text-align',
    'text-decoration',
    'font-weight',
    'font-style',
    'font-size',
    'font-family',
    'line-height',
    'vertical-align',
    'white-space'
]);

const FORMAT_CLASS_STYLES = {
    'editor-text-bold': { 'font-weight': 'bold' },
    'editor-text-italic': { 'font-style': 'italic' },
    'editor-text-underline': { 'text-decoration': 'underline' },
    'editor-text-strikethrough': { 'text-decoration': 'line-through' },
    'editor-text-underlineStrikethrough': { 'text-decoration': 'underline line-through' },
    'editor-text-code': {
        'font-family': 'Menlo, Consolas, Monaco, monospace',
        'font-size': '94%'
    }
};

const parseStyleDeclarations = style => {
    const entries = new Map();
    style.split(';').forEach(declaration => {
        const trimmed = declaration.trim();
        const separatorIndex = trimmed.indexOf(':');
        if (separatorIndex === -1) return;
        const property = trimmed.slice(0, separatorIndex).trim().toLowerCase();
        const value = trimmed.slice(separatorIndex + 1).trim();
        if (property && value) {
            entries.set(property, value);
        }
    });
    return entries;
};

const serializeStyleDeclarations = entries =>
    Array.from(entries.entries())
        .map(([property, value]) => `${property}: ${value}`)
        .join('; ');

const filterInlineStyle = node => {
    const style = node.getAttribute('style');
    if (!style) return;

    const safeEntries = new Map();
    parseStyleDeclarations(style).forEach((value, property) => {
        if (!SAFE_STYLE_PROPERTIES.has(property)) return;
        if (/url\(|expression\(|javascript:/i.test(value)) return;
        safeEntries.set(property, value);
    });

    if (safeEntries.size === 0) {
        node.removeAttribute('style');
    } else {
        node.setAttribute('style', serializeStyleDeclarations(safeEntries));
    }
};

const applyFormatClassStyles = node => {
    const className = node.getAttribute('class');
    if (!className) return;

    const styleEntries = parseStyleDeclarations(node.getAttribute('style') || '');

    className.split(/\s+/).forEach(cls => {
        const classStyles = FORMAT_CLASS_STYLES[cls];
        if (!classStyles) return;
        Object.entries(classStyles).forEach(([property, value]) => {
            if (!styleEntries.has(property)) {
                styleEntries.set(property, value);
            }
        });
    });

    if (styleEntries.size > 0) {
        node.setAttribute('style', serializeStyleDeclarations(styleEntries));
    }
};

const hardenAnnouncementNode = node => {
    if (node.tagName === 'A' && node.hasAttribute('target')) {
        node.setAttribute('rel', 'noopener noreferrer');
    }
    filterInlineStyle(node);
    applyFormatClassStyles(node);
};

export const sanitizeAnnouncementHtml = input => {
    if (!input) return '';
    DOMPurify.addHook('afterSanitizeAttributes', hardenAnnouncementNode);
    try {
        return DOMPurify.sanitize(input, ANNOUNCEMENT_HTML_CONFIG);
    } finally {
        DOMPurify.removeHook('afterSanitizeAttributes');
    }
};
